import React from "react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useStore } from "../../app/stores/store";
import { observer } from "mobx-react-lite";
import { PUBLIC_API_URL } from "../../environment";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDateTime } from "../../lib/utils";
import * as XLSX from "xlsx";
import saveAs from "file-saver";

type Props = {
  data: any[];
  rowsPerPageOptions: number[];
  pageSize?: number;
  status?: React.ReactNode;
  exceptColumns: string[];
  columnOrder?: string[];
  setSelectedId?: (id: string) => void;
  routeUrl: string;
  dialogFlags: any;
  getViewId?: (id: string) => void;
  onOpenRefundModal?: (booking: any) => void;
};

const customHeaders: Record<string, string> = {
  invoiceNo: "Invoice",
  createDate: "Date",
  createdBy: "Create",
  customerName: "Name",
  phone: "Phone",
  serviceName: "Service",
  nationalityName: "Nationality",
  refundRequests: "Refund",
  totalPrice: "Total Price",
  servicePrice: "Govt Price",
  centerFee: "Center Fee",
  paymentStatus: "Payment",
  bookingStatus: "Booking Status",
  adultsNumber: "Adult",
  childrenNumber: "Child",
  createdByRoles: "Role",
  customSecurityDeposit: "Custom Deposit",
  securityDeposit: "Deposit",
  // qty: "Qty",
};

const separateCamelCase = (text: string) =>
  text
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (str) => str.toUpperCase());

const TableReport = ({
  data,
  rowsPerPageOptions,
  pageSize,
  status,
  exceptColumns,
  columnOrder,
  setSelectedId,
  getViewId,
  onOpenRefundModal,
}: Props) => {
  const {
    userStore: { isAdmin, isPartner, isAirportCashier, isCashier, user },
  } = useStore();
  const [expandedRows, setExpandedRows] = React.useState<any>(null);

  const enrichedData = React.useMemo(() => {
    const servicesWithDeposit = ["Tourist Visa", "Tourist Visa KSA"];
    const safe = (val: any) => Number(val) || 0;

    return data
      .filter((item) => item.paymentStatus?.toLowerCase?.() === "completed")
      .map((item) => {
        const adults = safe(item.adultsNumber);
        const children = safe(item.childrenNumber);
        const qty = adults + children;
        const createdByRolesRaw = item.createdByRoles ?? [];
        const createdByRoles = Array.isArray(createdByRolesRaw)
          ? createdByRolesRaw
          : typeof createdByRolesRaw === "string"
            ? [createdByRolesRaw]
            : [];

        const isPartner = createdByRoles.includes("PARTNER");
        const isAirportCashier = createdByRoles.includes("AIRPORTCASHIER");
        const isCashier = createdByRoles.includes("CASHIER");

        // === Center Fees based on role ===
        let adultCenterFee = 0;
        let childCenterFee = 0;
        let regularPrice = 0;
        let childPrice = 0;

        if (isPartner) {
          // ✅ Extra discount if Partner and 60 days single entry
          if (item.entryType === "single" && item.duration === "60 days") {
            adultCenterFee = Math.max(0, safe(item.partnerCenterFee) + 34);
            childCenterFee = Math.max(0, safe(item.partnerChildCenterFee) + 59);
            regularPrice = Math.max(0, safe(item.servicePrice) + 121);
          } else {
            adultCenterFee = Math.max(0, safe(item.partnerCenterFee));
            childCenterFee = Math.max(0, safe(item.partnerChildCenterFee));
            regularPrice = Math.max(0, safe(item.servicePrice));
            childPrice = Math.max(0, safe(item.childPrice));
          }
        } else if (isAirportCashier) {
          if (item.entryType === "single" && item.duration === "60 days") {
            adultCenterFee = Math.max(0, safe(item.airportUserPrice) + 29);
            childCenterFee = Math.max(
              0,
              safe(item.airportUserChildPrice) + 109
            );
            regularPrice = Math.max(0, safe(item.servicePrice) + 121);
            childPrice = Math.max(0, safe(item.childPrice) + 41);
          } else {
            adultCenterFee = safe(item.airportUserPrice);
            childCenterFee = safe(item.airportUserChildPrice);
            regularPrice = Math.max(0, safe(item.servicePrice));
            childPrice = Math.max(0, safe(item.childPrice));
          }
        } else if (isCashier) {
          if (item.entryType === "single" && item.duration === "60 days") {
            adultCenterFee = Math.max(0, safe(item.centerFee) + 79);
            childCenterFee = Math.max(0, safe(item.childCenterFee) + 59);
            regularPrice = Math.max(0, safe(item.servicePrice) + 121);
          } else {
            adultCenterFee = safe(item.centerFee);
            childCenterFee = safe(item.childCenterFee);
            regularPrice = Math.max(0, safe(item.servicePrice));
            childPrice = Math.max(0, safe(item.childPrice));
          }
        } else {
          // Default to customer center fees
          adultCenterFee = safe(item.centerFee);
          childCenterFee = safe(item.childCenterFee);
          regularPrice = safe(item.servicePrice);
          childPrice = safe(item.childPrice);
        }

        const totalCenterFee =
          adultCenterFee * adults + childCenterFee * children;

        // === Base service price (for reporting reference only) ===
        // const regularPrice = safe(item.servicePrice);
        // const childPrice = safe(item.childPrice);

        const TotalservicePrice = regularPrice * adults + childPrice * children;

        // === Security deposit if applicable ===
        const hasDeposit = servicesWithDeposit.includes(item.serviceName);
        const securityDeposit = hasDeposit
          ? safe(item.securityDeposit) * qty
          : 0;

        return {
          ...item,
          qty,
          calculatedCenterFee: totalCenterFee,
          calculatedServicePrice: TotalservicePrice,
          securityDeposit,
        };
      });
  }, [data]);

  const priceBreakdownTemplate = (row: any) => {
    const safe = (val: any) => Number(val) || 0;

    const adults = safe(row.adultsNumber);
    const children = safe(row.childrenNumber);
    const qty = adults + children;

    const createdByRolesRaw = row.createdByRoles ?? [];
    const createdByRoles = Array.isArray(createdByRolesRaw)
      ? createdByRolesRaw
      : typeof createdByRolesRaw === "string"
        ? [createdByRolesRaw]
        : [];

    const isPartner = createdByRoles.includes("PARTNER");
    const isAirportCashier = createdByRoles.includes("AIRPORTCASHIER");
    const isCashier = createdByRoles.includes("CASHIER");

    // === Center Fees based on role ===
    let adultCenterFee = 0;
    let childCenterFee = 0;
    let regularPrice = 0;
    let childPrice = 0;

    if (isPartner) {
      // ✅ Extra discount if Partner and 60 days single entry
      if (row.entryType === "single" && row.duration === "60 days") {
        adultCenterFee = Math.max(0, safe(row.partnerCenterFee) + 34);
        childCenterFee = Math.max(0, safe(row.partnerChildCenterFee) + 59);
        regularPrice = Math.max(0, safe(row.servicePrice) + 121);
      } else {
        adultCenterFee = Math.max(0, safe(row.partnerCenterFee));
        childCenterFee = Math.max(0, safe(row.partnerChildCenterFee));
        regularPrice = Math.max(0, safe(row.servicePrice));
        childPrice = Math.max(0, safe(row.childPrice));
      }
    } else if (isAirportCashier) {
      if (row.entryType === "single" && row.duration === "60 days") {
        adultCenterFee = Math.max(0, safe(row.airportUserPrice) + 29);
        childCenterFee = Math.max(0, safe(row.airportUserChildPrice) + 109);
        regularPrice = Math.max(0, safe(row.servicePrice) + 121);
        childPrice = Math.max(0, safe(row.childPrice) + 41);
      } else {
        adultCenterFee = safe(row.airportUserPrice);
        childCenterFee = safe(row.airportUserChildPrice);
        regularPrice = Math.max(0, safe(row.servicePrice));
        childPrice = Math.max(0, safe(row.childPrice));
      }
    } else if (isCashier) {
      if (row.entryType === "single" && row.duration === "60 days") {
        adultCenterFee = Math.max(0, safe(row.centerFee) + 79);
        childCenterFee = Math.max(0, safe(row.childCenterFee) + 59);
        regularPrice = Math.max(0, safe(row.servicePrice) + 121);
      } else {
        adultCenterFee = safe(row.centerFee);
        childCenterFee = safe(row.childCenterFee);
        regularPrice = Math.max(0, safe(row.servicePrice));
        childPrice = Math.max(0, safe(row.childPrice));
      }
    } else {
      // Default to customer center fees
      adultCenterFee = safe(row.centerFee);
      childCenterFee = safe(row.childCenterFee);
      regularPrice = safe(row.servicePrice);
      childPrice = safe(row.childPrice);
    }

    // === Service Fees ===
    //const regularPrice = safe(row.servicePrice);
    //const childPrice = safe(row.childPrice);

    // === Total Calculations ===
    const totalAdultService = regularPrice * adults;
    const totalChildService = childPrice * children;
    const totalAdultCenter = adultCenterFee * adults;
    const totalChildCenter = childCenterFee * children;

    const servicesWithDeposit = ["Tourist Visa", "Tourist Visa KSA"];
    const hasDeposit = servicesWithDeposit.includes(row.serviceName);

    let securityDeposit = 0;

    const nationality = row.nationalityName?.trim();

    if (hasDeposit) {
      if (safe(row.customSecurityDeposit) > 0) {
        // 👈 Prefer custom deposit
        securityDeposit = safe(row.customSecurityDeposit);
      } else if (row.nationalityBasedDeposits && nationality) {
        const depositPerPerson = safe(
          row.nationalityBasedDeposits[nationality]
        );
        securityDeposit = depositPerPerson * qty;
      } else {
        securityDeposit = safe(row.securityDeposit);
      }
    }
    return (
      <div className="p-3 text-sm bg-gray-50 border rounded-md text-gray-800 shadow-inner mt-2">
        <strong className="block mb-2 text-black">Sales Price Details:</strong>
        <ul className="space-y-1">
          <li>
            🧑 Duration = <strong> {row.duration}</strong>
          </li>

          <li>
            🧑 Adults: {adults} × Gov Fee AED {regularPrice.toFixed(2)} ={" "}
            <strong>AED {totalAdultService.toFixed(2)}</strong>
          </li>
          <li>
            🧒 Children: {children} × Gov Fee AED {childPrice.toFixed(2)} ={" "}
            <strong>AED {totalChildService.toFixed(2)}</strong>
          </li>
          <li>
            🏢 Center Fee (Adult): {adults} × AED {adultCenterFee.toFixed(2)} ={" "}
            <strong>AED {totalAdultCenter.toFixed(2)}</strong>
          </li>
          <li>
            🏢 Center Fee (Child): {children} × AED {childCenterFee.toFixed(2)}{" "}
            = <strong>AED {totalChildCenter.toFixed(2)}</strong>
          </li>
          {securityDeposit > 0 && (
            <li>
              🔐 Security Deposit : AED <strong>{securityDeposit}</strong>
            </li>
          )}

          <li>🏢Nationality : {nationality}</li>
        </ul>
      </div>
    );
  };

  const columns = React.useMemo(() => {
    if (!enrichedData?.length) return [];
    const keys = Object.keys(enrichedData[0]).filter(
      (k) => !exceptColumns.includes(k)
    );
    return columnOrder && columnOrder.length
      ? [...columnOrder, ...keys.filter((k) => !columnOrder.includes(k))]
      : keys;
  }, [enrichedData, exceptColumns, columnOrder]);

  const downloadInvoice = async (id: string) => {
    if (!id) return alert("Missing Booking ID");
    try {
      const res = await fetch(`${PUBLIC_API_URL}booking/${id}/booking-invoice`);
      if (!res.ok) throw new Error(res.statusText);
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `invoice_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      console.error("Invoice download failed", e);
      alert("Failed to download invoice");
    }
  };

  const exportTableToPdf = async (
    data: any[],
    columns: string[],
    customHeaders: Record<string, string>,
    printedBy: string // ✅ just pass the name
  ) => {
    const doc = new jsPDF({ orientation: "landscape" });
    const logoUrl = "https://uberevisa.com/assets/img/UBER-01-Feature.png";

    // Fetch and convert logo to base64
    const logoBase64 = await fetch(logoUrl)
      .then((res) => res.blob())
      .then(
        (blob) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      )
      .catch((err) => {
        console.error("Error fetching logo:", err);
        return "";
      });
    const marginLeft = 10;
    const pageWidth = doc.internal.pageSize.getWidth();

    // Add logo
    let currentY = 10;
    if (logoBase64) {
      doc.addImage(logoBase64, "PNG", 10, 10, 50, 15);
    }

    const today = new Date().toLocaleString("en-GB", { hour12: false });

    // Date on first line
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    const dateText = `Date: ${today}`;
    const dateWidth = doc.getTextWidth(dateText);
    doc.text(dateText, pageWidth - dateWidth - marginLeft, currentY + 10);

    // Printed by on next line
    const printedByText = `Printed by: ${printedBy}`;
    const printedByWidth = doc.getTextWidth(printedByText);
    doc.text(
      printedByText,
      pageWidth - printedByWidth - marginLeft,
      currentY + 18
    ); // 8 units lower

    currentY += 25; // leave space below logo & header

    // 👇 Add some spacing after logo
    const tableStartY = 35; // adjust as needed
    // 👇 Enrich the data like in useMemo
    const servicesWithDeposit = ["Tourist Visa", "Tourist Visa KSA"];
    const enrichedData = data.map((item) => {
      const adults = Number(item.adultsNumber) || 0;
      const children = Number(item.childrenNumber) || 0;
      const qty = adults + children;

      const regularPrice = parseFloat(item.servicePrice ?? "0");
      const childPrice = parseFloat(item.childPrice ?? "0");
      const totalPrice = parseFloat(item.totalPrice ?? "0");

      const TotalservicePrice = regularPrice * adults + childPrice * children;

      const hasDeposit = servicesWithDeposit.includes(item.serviceName);
      const securityDeposit = hasDeposit
        ? parseFloat(item.securityDeposit ?? "0")
        : 0;

      const centerFee = totalPrice - TotalservicePrice - securityDeposit;

      return {
        ...item,
        // qty,
        centerFee,
        servicePrice: TotalservicePrice,
        securityDeposit,
      };
    });

    // 👇 Prepare headers and body
    const headers = columns.map(
      (key) => customHeaders[key] ?? separateCamelCase(key)
    );
    const dataRows = enrichedData.map((row) =>
      columns.map((key) => {
        if (key === "refundRequests") {
          return row.refundRequests?.some((r: any) => r.status === 1)
            ? "Refunded"
            : "Pending";
        }
        if (key === "paymentStatus") {
          return row.paymentStatus === "Completed" ? "Paid" : "Unpaid";
        }
        if (key === "servicePrice") {
          return ` ${row.calculatedServicePrice?.toFixed(2)}`;
        }

        if (key === "centerFee") {
          return ` ${row.calculatedCenterFee?.toFixed(2)}`;
        }
        if (key === "createDate") {
          const date = new Date(row.createDate);
          return date.toLocaleDateString("en-GB"); // dd/mm/yyyy
        }
        if (key === "qty") {
          return row.qty;
        }
        if (typeof row[key] === "object") {
          return JSON.stringify(row[key]);
        }
        return row[key] ?? "";
      })
    );

    // 👇 Summary totals
    // 👇 Summary totals
    const totalSales = enrichedData.reduce(
      (sum, b) => sum + Number(b.calculatedServicePrice ?? b.servicePrice ?? 0),
      0
    );

    const totalCenterFees = enrichedData.reduce(
      (sum, b) => sum + (Number(b.calculatedCenterFee) || 0),
      0
    );

    const totalRefunds = enrichedData
      .flatMap((b) => b.refundRequests || [])
      .reduce((sum, r) => sum + (r.requestedAmount ?? 0), 0);

    const totalProfit = totalCenterFees;

    // 👇 Render table
    autoTable(doc, {
      head: [headers],
      body: dataRows,
      startY: tableStartY, // 👈 pushes table below logo
      margin: { left: 2, right: 2 }, // 👈 Controls horizontal space
      styles: {
        fontSize: 9,
        overflow: "visible",
      },
      columnStyles: {
        default: { cellWidth: "auto" },
      },
      headStyles: {
        fillColor: [204, 48, 147], // RGB for blue
        textColor: 255, // White text
        fontStyle: "bold",
      },
      didDrawPage: (dataArg) => {
        const pageHeight = doc.internal.pageSize.getHeight();
        const footerText =
          "Website: uberevisa.com | Email: info@uberevisa.com | Phone: 043426666";

        doc.setFontSize(10);
        doc.setTextColor(100); // gray
        doc.text(
          footerText,
          doc.internal.pageSize.getWidth() / 2,
          pageHeight - 10,
          {
            align: "center",
          }
        );
      },
    });

    const finalY = (doc as any).lastAutoTable.finalY || 40;

    // 👇 Add totals to PDF
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Total Sales: AED ${totalSales.toFixed(2)}`, 14, finalY + 15);
    // doc.text(`Total Refunds: AED ${totalRefunds.toFixed(2)}`, 14, finalY + 35);
    doc.text(`Total Profit: AED ${totalProfit.toFixed(2)}`, 14, finalY + 25);

    // After rendering the table and totals
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerText =
      "Website: uberevisa.com | Email: info@uberevisa.com | Phone: 043426666";
    doc.setFontSize(10);
    doc.setTextColor(100); // Gray color
    doc.text(
      footerText,
      doc.internal.pageSize.getWidth() / 2,
      pageHeight - 10,
      {
        align: "center",
      }
    );
    // 👇 Save the file
    doc.save("Booking_Report.pdf");
  };

  function exportTableToExcel(
    enrichedData: any[],
    columns: string[],
    customHeaders: Record<string, string>,
    printedBy: string
  ): void {
    // Prepare data for Excel
    const dataForExcel = enrichedData.map((row) => {
      const obj: Record<string, any> = {};
      columns.forEach((key) => {
        let value = row[key];

        if (key === "refundRequests") {
          value = row.refundRequests?.some((r: any) => r.status === 1)
            ? "Refunded"
            : "Pending";
        }
        if (key === "paymentStatus") {
          value = row.paymentStatus === "Completed" ? "Paid" : "Unpaid";
        }
        if (key === "servicePrice") {
          value = row.calculatedServicePrice?.toFixed(2) ?? row.servicePrice;
        }
        if (key === "centerFee") {
          value = row.calculatedCenterFee?.toFixed(2);
        }
        if (key === "createDate") {
          value = new Date(row.createDate).toLocaleDateString("en-GB");
        }
        if (key === "qty") {
          value = row.qty;
        }
        if (typeof value === "object") {
          value = JSON.stringify(value);
        }

        obj[customHeaders[key] ?? key] = value ?? "";
      });
      return obj;
    });

    // Header info rows
    const headerInfo = [
      ["Exported by: " + printedBy],
      ["Date: " + new Date().toLocaleString("en-GB", { hour12: false })],
    ];

    // Table headers row
    const tableHeaders = [columns.map((key) => customHeaders[key] ?? key)];

    // Create worksheet with data (skipHeader true because we add headers manually)
    const ws = XLSX.utils.json_to_sheet(dataForExcel, { skipHeader: true });

    // Add header info and table headers on top
    XLSX.utils.sheet_add_aoa(ws, [...headerInfo, ...tableHeaders], {
      origin: "A1",
    });

    // Calculate last column letter for merging (e.g., if columns.length=12 -> "L")
    const lastColLetter = XLSX.utils.encode_col(columns.length - 1);

    // Merge first row (Exported by) across all columns
    if (!ws["!merges"]) ws["!merges"] = [];
    ws["!merges"].push({
      s: { r: 0, c: 0 }, // start row 0, col 0 (A1)
      e: { r: 0, c: columns.length - 1 }, // end col = last column
    });

    // Merge second row (Date) across all columns
    ws["!merges"].push({
      s: { r: 1, c: 0 }, // row 1, col 0 (A2)
      e: { r: 1, c: columns.length - 1 }, // same last column
    });

    // Style header info rows (merged cells)
    for (let r = 0; r < 2; r++) {
      const cellAddress = XLSX.utils.encode_cell({ r, c: 0 }); // merged cell always at col 0
      if (ws[cellAddress]) {
        ws[cellAddress].s = {
          font: { bold: true, sz: 12 },
          alignment: { horizontal: "center", vertical: "center" },
        };
      }
    }

    // Style table headers (row 3)
    const headerRowIndex = 2;
    for (let c = 0; c < columns.length; c++) {
      const cellAddress = XLSX.utils.encode_cell({ r: headerRowIndex, c });
      if (ws[cellAddress]) {
        ws[cellAddress].s = {
          font: { bold: true },
          alignment: { horizontal: "center", vertical: "center" },
          fill: { fgColor: { rgb: "D9E1F2" } }, // light blue background
        };
      }
    }

    // Optional: set column widths
    ws["!cols"] = columns.map(() => ({ wpx: 120 }));

    // Create workbook and save
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bookings");

    const excelBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(
      blob,
      `Booking_Report_${new Date().toISOString().replace(/[:.]/g, "-")}.xlsx`
    );
  }

  return (
    <>
      <div className="flex justify-end mb-3 space-x-2">
        {/* Export PDF Button */}
        <button
          className="btn btn-primary"
          onClick={() =>
            exportTableToPdf(
              enrichedData,
              columns.filter(
                (key) => key !== "bookingStatus" && key !== "createdByRoles"
              ),
              customHeaders,
              user?.displayName || "Unknown"
            )
          }
        >
          Export PDF
        </button>

        {/* Export Excel Button */}
        <button
          className="btn btn-secondary"
          onClick={() =>
            exportTableToExcel(
              enrichedData,
              columns.filter(
                (key) => key !== "bookingStatus" && key !== "createdByRoles"
              ),
              customHeaders,
              user?.displayName || "Unknown"
            )
          }
        >
          Export Excel
        </button>
      </div>

      <DataTable
        className="p-datatable-sm"
        value={enrichedData}
        rows={pageSize ?? 10}
        rowsPerPageOptions={rowsPerPageOptions}
        tableStyle={{ fontSize: "0.875rem" }}
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={priceBreakdownTemplate}
      >
        {columns.map((key) => (
          <Column
            key={key}
            field={key}
            header={customHeaders[key] ?? separateCamelCase(key)}
            body={(row: any) => {
              if (key === "invoiceNo") {
                return (
                  <span
                    onClick={() => downloadInvoice(row.id)}
                    className="text-red-500 hover:text-red-700 cursor-pointer underline"
                  >
                    {row[key]}
                  </span>
                );
              }

              if (key === "customerName") {
                const name =
                  row[key]?.split(" ").slice(0, 2).join(" ") || "No Name";
                return (
                  <span
                    onClick={() => getViewId?.(row.id)}
                    className="cursor-pointer text-black hover:text-gray-700"
                  >
                    {name}
                  </span>
                );
              }

              if (key === "paymentStatus") {
                const isPaid =
                  row[key] === "Completed" || row[key] === "completed";
                return (
                  <span
                    className={`font-bold ${isPaid ? "text-green-600" : "text-red-500"}`}
                  >
                    {isPaid ? "Paid" : "Unpaid"}
                  </span>
                );
              }

              if (key === "createDate") {
                return (
                  <span>
                    {new Date(row.createDate).toLocaleDateString("en-GB")}
                  </span>
                );
              }

              if (key === "bookingStatus") {
                const statusLabel =
                  row[key] === 1 || row[key]?.toLowerCase?.() === "completed"
                    ? "Completed"
                    : "Pending";
                return (
                  <span
                    className={`font-semibold ${
                      statusLabel === "Completed"
                        ? "text-green-600"
                        : "text-yellow-600"
                    }`}
                  >
                    {statusLabel}
                  </span>
                );
              }

              if (key === "refundRequests") {
                const isRefunded = row.refundRequests?.some(
                  (r: any) => r.status === 1
                );
                return (
                  <button
                    type="button"
                    className={`btn btn-sm ${isRefunded ? "btn-success" : "btn-outline-primary"}`}
                    onClick={() => onOpenRefundModal?.(row)}
                  >
                    {isRefunded ? "Refunded" : "View"}
                  </button>
                );
              }

              if (key === "servicePrice") {
                return ` ${row.calculatedServicePrice?.toFixed(2)}`;
              }

              if (key === "centerFee") {
                return ` ${row.calculatedCenterFee?.toFixed(2)}`;
              }

              if (key === "qty") {
                return row.qty;
              }

              return row[key];
            }}
            style={{ padding: "8px" }}
          />
        ))}
        {status && <Column field="status" header="Status" body={status} />}

        <Column expander style={{ width: "1em" }} />
      </DataTable>
    </>
  );
};

export default observer(TableReport);
